/**
 * RaceDay Firestore data model schema.
 *
 * Collection: portal_race_days/{docId}
 * Subcollection: portal_race_days/{docId}/audit_log/{logId}
 *
 * Design principles:
 * - Corrections are new entries with `supersedes` pointing to the original (append-only)
 * - Every mutation creates an audit_log entry (timestamp, author, action, before/after)
 * - Audit log lives in a subcollection to avoid 1MB document limit
 * - Security rules use `clubId` (camelCase) to match portal_invites convention
 * - RaceOfficer gets write access to race day data (additive rule change)
 */

// ── Collection path ─────────────────────────────────────────────────
export const RACE_DAYS_COLLECTION = 'portal_race_days';
export const AUDIT_LOG_SUBCOLLECTION = 'audit_log';

// ── Status values ───────────────────────────────────────────────────
export const RACE_DAY_STATUS = {
  SCHEDULED: 'scheduled',
  RACING: 'racing',
  COMPLETED: 'completed',
  ABANDONED: 'abandoned',
  POSTPONED: 'postponed',
};

// ── Officer roles ───────────────────────────────────────────────────
export const OFFICER_ROLE = {
  PRO: 'pro', // Principal Race Officer
  OOD: 'ood', // Officer of the Day
  MARK_LAYER: 'mark_layer',
  SAFETY: 'safety',
  TIMER: 'timer',
  SCORER: 'scorer',
};

// ── Decision types ──────────────────────────────────────────────────
export const DECISION_TYPE = {
  POSTPONE: 'postpone',
  ABANDON: 'abandon',
  SHORTEN: 'shorten',
  GENERAL_RECALL: 'general_recall',
  COURSE_CHANGE: 'course_change',
  FLAG_SIGNAL: 'flag_signal',
  OTHER: 'other',
};

// ── Fleet start types ───────────────────────────────────────────────
export const START_TYPE = {
  NORMAL: 'normal',
  GENERAL_RECALL: 'general_recall',
  INDIVIDUAL_RECALL: 'individual_recall',
  POSTPONED: 'postponed',
  ABANDONED: 'abandoned',
};

// ── Finish status values ────────────────────────────────────────────
export const FINISH_STATUS = {
  FINISHED: 'finished',
  DNF: 'dnf',
  DNS: 'dns',
  DSQ: 'dsq',
  OCS: 'ocs',
  RAF: 'raf',
  RET: 'ret',
};

// ── Broadcast channels ──────────────────────────────────────────────
export const BROADCAST_CHANNEL = {
  VHF: 'vhf',
  FLAG: 'flag',
  SOUND: 'sound',
  APP: 'app',
};

// ── Sea state values ────────────────────────────────────────────────
export const SEA_STATE = {
  CALM: 'calm',
  SLIGHT: 'slight',
  MODERATE: 'moderate',
  ROUGH: 'rough',
};

// ── Visibility values ───────────────────────────────────────────────
export const VISIBILITY = {
  GOOD: 'good',
  MODERATE: 'moderate',
  POOR: 'poor',
};

// ── Audit actions ───────────────────────────────────────────────────
export const AUDIT_ACTION = {
  CREATE_RACE_DAY: 'create_race_day',
  UPDATE_STATUS: 'update_status',
  ADD_CONDITION: 'add_condition',
  ADD_OFFICER: 'add_officer',
  ADD_DECISION: 'add_decision',
  ADD_FLEET_START: 'add_fleet_start',
  UPDATE_FLEET_START: 'update_fleet_start',
  SET_COURSE: 'set_course',
  ADD_FINISH_RECORD: 'add_finish_record',
  ADD_BROADCAST: 'add_broadcast',
  CORRECT_ENTRY: 'correct_entry',
};

/**
 * @typedef {Object} RaceDay
 * @property {string} clubId           - FK to clubs/{clubId} (camelCase for security rules)
 * @property {string} event_id         - FK to clubs/{clubId}/events/{eventId}
 * @property {string} date             - ISO date string "YYYY-MM-DD"
 * @property {string} status           - One of RACE_DAY_STATUS values
 * @property {Condition[]} conditions   - Weather/sea observations (append-only)
 * @property {Officer[]} officers       - Race officials on duty (append-only)
 * @property {Decision[]} decisions     - Committee decisions (append-only)
 * @property {FleetStart[]} fleet_starts - Start records per fleet/race (append-only)
 * @property {CourseSetup|null} course_setup - Current course configuration
 * @property {FinishRecord[]} finish_records - Finish times per boat (append-only)
 * @property {Broadcast[]} broadcasts   - Communications sent (append-only)
 * @property {import('firebase/firestore').Timestamp} created_at
 * @property {string} created_by        - UID of creator
 * @property {import('firebase/firestore').Timestamp} updated_at
 * @property {string} updated_by        - UID of last modifier
 */

/**
 * @typedef {Object} Condition
 * @property {string} id                  - Unique entry ID (nanoid)
 * @property {import('firebase/firestore').Timestamp} recorded_at
 * @property {string} recorded_by         - UID
 * @property {number} [wind_speed_kts]
 * @property {number} [wind_direction_deg] - 0-360 degrees true
 * @property {string} [sea_state]          - One of SEA_STATE values
 * @property {string} [visibility]         - One of VISIBILITY values
 * @property {number} [temperature_c]
 * @property {string} [notes]
 * @property {string|null} [supersedes]    - ID of entry this corrects
 */

/**
 * @typedef {Object} Officer
 * @property {string} id
 * @property {string} role               - One of OFFICER_ROLE values
 * @property {string} name
 * @property {string} [contact]
 * @property {import('firebase/firestore').Timestamp} assigned_at
 * @property {string} assigned_by        - UID
 * @property {string|null} [supersedes]
 */

/**
 * @typedef {Object} Decision
 * @property {string} id
 * @property {string} type               - One of DECISION_TYPE values
 * @property {string} description
 * @property {import('firebase/firestore').Timestamp} made_at
 * @property {string} made_by            - UID
 * @property {string[]} [flags]          - Flag signals raised
 * @property {string|null} [supersedes]
 */

/**
 * @typedef {Object} FleetStart
 * @property {string} id
 * @property {string} fleet_id           - FK to fleet_config fleets[].id
 * @property {string[]} class_ids        - FK to fleet_config classes[].id
 * @property {number} race_number        - Race number within the day (1, 2, ...)
 * @property {number} sequence           - Order in start sequence
 * @property {string} scheduled_start    - "HH:MM" planned warning signal
 * @property {import('firebase/firestore').Timestamp} [actual_start]
 * @property {string} start_type         - One of START_TYPE values
 * @property {string[]} [recalled_boats] - Sail numbers of recalled boats
 * @property {string} [notes]
 * @property {string} recorded_by        - UID
 * @property {import('firebase/firestore').Timestamp} recorded_at
 * @property {string|null} [supersedes]
 */

/**
 * @typedef {Object} CourseSetup
 * @property {string} [course_card_id]   - FK to courseBundles course card
 * @property {string} [course_number]    - Selected course from the card
 * @property {CourseMarkPosition[]} marks - Laid mark positions
 * @property {number} [distance_nm]      - Approximate course distance
 * @property {number} [laps]
 * @property {number} [wind_direction_deg] - Wind direction when course was set
 * @property {string} set_by             - UID
 * @property {import('firebase/firestore').Timestamp} set_at
 */

/**
 * @typedef {Object} CourseMarkPosition
 * @property {string} mark_id           - FK to clubs/{clubId}/marks/{markId}
 * @property {string} name
 * @property {number} [lat]
 * @property {number} [lng]
 */

/**
 * @typedef {Object} FinishRecord
 * @property {string} id
 * @property {string} fleet_id
 * @property {number} race_number
 * @property {string} sail_number
 * @property {string} [boat_name]
 * @property {import('firebase/firestore').Timestamp} finish_time
 * @property {number} [elapsed_seconds]
 * @property {number} [corrected_seconds]
 * @property {string} status             - One of FINISH_STATUS values
 * @property {number} [position]         - Finishing position (1-based)
 * @property {string} [notes]
 * @property {string} recorded_by        - UID
 * @property {import('firebase/firestore').Timestamp} recorded_at
 * @property {string|null} [supersedes]
 */

/**
 * @typedef {Object} Broadcast
 * @property {string} id
 * @property {string} channel            - One of BROADCAST_CHANNEL values
 * @property {string} message
 * @property {import('firebase/firestore').Timestamp} sent_at
 * @property {string} sent_by            - UID
 * @property {string|null} [supersedes]
 */

/**
 * @typedef {Object} AuditLogEntry
 * @property {import('firebase/firestore').Timestamp} timestamp
 * @property {string} author             - UID
 * @property {string} action             - One of AUDIT_ACTION values
 * @property {string} field              - Array/field that was modified
 * @property {string} [entry_id]         - ID of the specific entry added/corrected
 * @property {*} [before]                - JSON snapshot before (null for new entries)
 * @property {*} after                   - JSON snapshot after
 */

/**
 * Create an empty RaceDay document shape.
 *
 * @param {Object} params
 * @param {string} params.clubId
 * @param {string} params.eventId
 * @param {string} params.date - ISO date "YYYY-MM-DD"
 * @param {string} params.createdBy - UID
 * @returns {Object} Firestore-ready document data (timestamps added by caller)
 */
export function emptyRaceDay({ clubId, eventId, date, createdBy }) {
  return {
    clubId,
    event_id: eventId,
    date,
    status: RACE_DAY_STATUS.SCHEDULED,
    conditions: [],
    officers: [],
    decisions: [],
    fleet_starts: [],
    course_setup: null,
    finish_records: [],
    broadcasts: [],
    created_by: createdBy,
    updated_by: createdBy,
    // created_at and updated_at should use serverTimestamp() at write time
  };
}
